/**
 * Cookie utility functions for cross-domain authentication
 * Cookies with domain=.runow.ai are shared across app.runow.ai and runow.ai
 */

const COOKIE_DOMAIN = '.runow.ai';
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

/**
 * Determine if we should set cookies with domain restriction
 */
function shouldUseDomainCookie(): boolean {
  const hostname = window.location.hostname;
  
  // Never use domain cookies for localhost or CloudFront URLs
  if (hostname === 'localhost' || 
      hostname.includes('localhost') || 
      hostname === '127.0.0.1' ||
      hostname.includes('cloudfront.net')) {
    return false;
  }
  
  // Use domain cookies for runow.ai domains (prod and dev subdomains)
  return hostname.includes('runow.ai');
}

/**
 * Set a cookie with domain=.runow.ai for cross-subdomain access
 */
export function setCookie(name: string, value: string, maxAge: number = COOKIE_MAX_AGE): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + maxAge * 1000);
  
  const useDomainCookie = shouldUseDomainCookie();
  
  if (useDomainCookie) {
    // For runow.ai domains, set cookie with domain=.runow.ai so it's accessible from both app.runow.ai and runow.ai
    document.cookie = `${name}=${value}; domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}; secure; samesite=lax`;
  } else {
    // For localhost or CloudFront URLs, set cookie without domain restriction
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; samesite=lax`;
  }
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=');
    acc[key] = decodeURIComponent(value);
    return acc;
  }, {} as Record<string, string>);
  
  return cookies[name] || null;
}

/**
 * Delete a cookie
 */
export function deleteCookie(name: string): void {
  const useDomainCookie = shouldUseDomainCookie();
  
  if (useDomainCookie) {
    // For runow.ai domains, delete from both current domain and .runow.ai
    document.cookie = `${name}=; domain=${COOKIE_DOMAIN}; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } else {
    // For localhost or CloudFront URLs, delete from current domain only
    document.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}
